// Profile API queries - College Details, Coordinator Profile, Claims
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { API_ENDPOINTS } from '../endpoints';
import { authService } from '@/services/auth.service';
import type { CollegeProfile, CoordinatorProfile, CollegeClaims } from '@/types/auth.types';
import type {
  CollegeDetailModel,
  UpdateCoordinatorRequest,
  UpdateCollegeDetailsRequest,
  AddressByPincodeResponse,
  UploadDocumentResponse,
  RoleItem,
  PermissionItem,
  GetDefaultPermissionsResponse,
  UserPermissionsResponse,
  InvitePlacementCoordinatorRequest,
  TpoSettingsModel,
} from '@/features/settings/types';

// Re-export settings types for backward compatibility
export type {
  UpdateCoordinatorRequest,
  UpdateCollegeDetailsRequest,
  AddressByPincodeResponse,
  UploadDocumentResponse,
  RoleItem,
  PermissionItem,
  GetDefaultPermissionsResponse,
  UserPermissionsResponse,
  InvitePlacementCoordinatorRequest,
  TpoSettingsModel,
} from '@/features/settings/types';

// Query keys
export const profileKeys = {
  all: ['profile'] as const,
  collegeDetails: () => [...profileKeys.all, 'college'] as const,
  coordinatorProfile: () => [...profileKeys.all, 'coordinator'] as const,
  claims: () => [...profileKeys.all, 'claims'] as const,
};

// API Response types - matches exact API structure
interface CollegeDetailsApiResponse extends CollegeDetailModel {
  collegeName: string;
}

interface CoordinatorProfileApiResponse {
  uId: string;
  collegeUId?: string;
  invitorUId?: string;
  profileImage?: string;
  designation?: string;
  coordinatorFirstName?: string;
  coordinatorLastName?: string;
  coordinatorEmail?: string;
  coordinatorMobile?: string;
  role?: string;
  collegeName?: string;
  collegeEmail?: string;
}

interface ClaimsApiResponse {
  uId?: string;
  collegeUId?: string;
  collegeClaims?: Array<{
    displayName?: string;
    name?: string;
    value?: boolean;
  }>;
}

// Transform API response to our types
const transformCollegeDetails = (data: CollegeDetailsApiResponse): CollegeProfile => ({
  uid: data.uId,
  collegeName: data.collegeName || '',
  status: data.status || 'pending',
  experiencedOrgUId: data.experiencedOrgUId,
  collegeAdminUId: data.collegeAdminUId,
  collegeEmail: data.collegeEmail,
  collegeAdminName: data.collegeAdminName,
  collegeAdminEmail: data.collegeAdminEmail,
  address: data.address,
  collegeRegistrationNumber: data.collegeRegistrationNumber,
  universityName: data.collegeUinversityName,
  contactNumber: data.contactNumber,
  website: data.website,
  establishedYear: data.establishedYear,
  description: data.description || data.collegeDescription,
  totalStudents: data.totalStudents,
  documents: data.document,
  isVerified: data.isVerified,
  profileImage: data.profileImage,
  collegeLogo: data.collegeLogo,
  placementCoordinators: data.collegePlacementCooardinator,
  isCoursesAdded: data.isCoursesAdded,
  socialMediaModels: data.socialMediaModels,
  isRegisteredByCandidate: data.isRegisteredByCandidate,
  requestedOn: data.requestedOn,
  lastLoginTime: data.lastLoginTime,
  isTest: data.isTest,
});

const transformCoordinatorProfile = (data: CoordinatorProfileApiResponse): CoordinatorProfile => ({
  uid: data.uId,
  collegeUId: data.collegeUId,
  invitorUId: data.invitorUId,
  firstName: data.coordinatorFirstName || '',
  lastName: data.coordinatorLastName || '',
  coordinatorEmail: data.coordinatorEmail || '',
  phoneNumber: data.coordinatorMobile,
  profileImage: data.profileImage,
  designation: data.designation,
  role: data.role,
  collegeName: data.collegeName,
  collegeEmail: data.collegeEmail,
});

const transformClaims = (data: ClaimsApiResponse): CollegeClaims => ({
  uId: data.uId,
  collegeUId: data.collegeUId,
  claims: (data.collegeClaims || []).map((claim) => ({
    displayName: claim.displayName,
    name: claim.name || '',
    value: claim.value || false,
  })),
});

// Fetch college details by UID
export const fetchCollegeDetails = async (): Promise<CollegeProfile> => {
  const organizationUId = authService.getOrganizationUid();

  if (!organizationUId) {
    throw new Error('Organization UID not found');
  }

  const response = await api.college.post<CollegeDetailsApiResponse>(
    API_ENDPOINTS.college.getCollegeDetailsByUId,
    { uId: organizationUId }
  );

  // Update status in storage
  if (response.data?.status) {
    authService.setStatus(response.data.status);
  }


  return transformCollegeDetails(response.data);
};

// Fetch coordinator profile by UID
export const fetchCoordinatorProfile = async (): Promise<CoordinatorProfile> => {
  const userUId = authService.getUserUid();

  if (!userUId) {
    throw new Error('User UID not found');
  }

  const response = await api.college.post<CoordinatorProfileApiResponse>(
    API_ENDPOINTS.college.getPlacementCoordinatorByUId,
    { uId: userUId }
  );

  return transformCoordinatorProfile(response.data);
};

// Fetch claims by college UID
export const fetchCollegeClaims = async (): Promise<CollegeClaims> => {
  const organizationUId = authService.getOrganizationUid();

  if (!organizationUId) {
    throw new Error('Organization UID not found');
  }

  const response = await api.admin.post<ClaimsApiResponse>(
    API_ENDPOINTS.admin.getClaimsByCollegeUId,
    {
      apiSecurityKey: import.meta.env.VITE_API_SECURITY_KEY || '',
      uId: organizationUId,
    }
  );

  return transformClaims(response.data);
};

// React Query hooks
export const useCollegeDetails = (enabled = true) => {
  return useQuery({
    queryKey: profileKeys.collegeDetails(),
    queryFn: fetchCollegeDetails,
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache after unmount
    refetchOnMount: false, // Use cache if data is fresh
    refetchOnWindowFocus: true,
    retry: 2,
  });
};

export const useCoordinatorProfile = (enabled = true) => {
  return useQuery({
    queryKey: profileKeys.coordinatorProfile(),
    queryFn: fetchCoordinatorProfile,
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache after unmount
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    retry: 2,
  });
};

export const useCollegeClaims = (enabled = true) => {
  return useQuery({
    queryKey: profileKeys.claims(),
    queryFn: fetchCollegeClaims,
    enabled,
    staleTime: 1000 * 60 * 10, // 10 minutes - claims change less frequently
    gcTime: 1000 * 60 * 60, // 60 minutes - claims rarely change
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// Combined hook to fetch all profile data
export const useProfileData = (enabled = true) => {
  const collegeDetails = useCollegeDetails(enabled);
  const coordinatorProfile = useCoordinatorProfile(enabled);
  const claims = useCollegeClaims(enabled);

  return {
    collegeProfile: collegeDetails.data,
    coordinatorProfile: coordinatorProfile.data,
    claims: claims.data,
    isLoading: collegeDetails.isLoading || coordinatorProfile.isLoading || claims.isLoading,
    isError: collegeDetails.isError || coordinatorProfile.isError || claims.isError,
    refetchAll: () => {
      collegeDetails.refetch();
      coordinatorProfile.refetch();
      claims.refetch();
    },
  };
};

// Hook to invalidate all profile queries
export const useInvalidateProfileData = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: profileKeys.all });
  };
};

// Helper to check if user has a specific claim
export const hasClaimValue = (claims: CollegeClaims | undefined, claimName: string): boolean => {
  if (!claims?.claims) return false;
  const claim = claims.claims.find((c) => c.name === claimName);
  return claim?.value || false;
};

// ===== UPDATE MUTATIONS =====

// Update Coordinator Profile
export const updateCoordinatorProfile = async (
  data: UpdateCoordinatorRequest
): Promise<CoordinatorProfile> => {
  const response = await api.college.post<CoordinatorProfileApiResponse>(
    API_ENDPOINTS.college.updatePlacementCoordinator,
    data
  );

  return transformCoordinatorProfile(response.data);
};

// Update College Details
export const updateCollegeDetails = async (
  data: UpdateCollegeDetailsRequest
): Promise<CollegeProfile> => {
  const response = await api.college.post<CollegeDetailsApiResponse>(
    API_ENDPOINTS.college.updateCollegeDetails,
    data
  );

  return transformCollegeDetails(response.data);
};

// Mutation hook for updating coordinator profile
export const useUpdateCoordinatorProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCoordinatorProfile,
    onSuccess: () => {
      // Refetch coordinator profile to get fresh data
      queryClient.invalidateQueries({ queryKey: profileKeys.coordinatorProfile() });
    },
  });
};

// Mutation hook for updating college details
export const useUpdateCollegeDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCollegeDetails,
    onSuccess: () => {
      // Refetch college details to get fresh data
      queryClient.invalidateQueries({ queryKey: profileKeys.collegeDetails() });
    },
  });
};

// ===== ADDRESS LOOKUP =====

// Fetch address by pincode
export const fetchAddressByPincode = async (pincode: string): Promise<AddressByPincodeResponse> => {
  const response = await api.admin.post<AddressByPincodeResponse>(
    API_ENDPOINTS.admin.getAddressByPincode,
    {
      pincode,
      apiSecurityKey: import.meta.env.VITE_API_SECURITY_KEY || '',
    }
  );


  // Normalize field names - API might return different casing
  const data = response.data;
  return {
    city: data.city || (data as Record<string, string>).City || (data as Record<string, string>).CITY,
    state: data.state || (data as Record<string, string>).State || (data as Record<string, string>).STATE,
    country: data.country || (data as Record<string, string>).Country || (data as Record<string, string>).COUNTRY,
    district: data.district || (data as Record<string, string>).District || (data as Record<string, string>).DISTRICT,
    taluka: data.taluka || (data as Record<string, string>).Taluka || (data as Record<string, string>).TALUKA,
    pincode: data.pincode || (data as Record<string, string>).Pincode || (data as Record<string, string>).PINCODE,
  };
};

// ===== FILE UPLOAD =====

// Upload college document (profile image, logo, etc.)
export const uploadCollegeDocument = async (
  file: File,
  collegeUId: string,
  documentType: string = 'profile'
): Promise<string> => {
  const formData = new FormData();
  // Use 'Document' as field name with filename to match Flutter API format
  // Flutter uses: http.MultipartFile.fromBytes('Document', file, filename: documentName)
  formData.append('Document', file, file.name);
  formData.append('CollegeUId', collegeUId);
  formData.append('DocumentName', file.name);
  formData.append('DocumentType', documentType);

  const response = await api.college.upload<UploadDocumentResponse>(
    API_ENDPOINTS.college.uploadCollegeDocument,
    formData
  );

  // API returns documentURL (capital URL) in data object
  const uploadedUrl = response.data?.documentURL;

  if (!uploadedUrl) {
    throw new Error('No URL returned from upload API');
  }

  return uploadedUrl;
};

// ===== TEAM MANAGEMENT APIs =====

// Fetch all roles
export const fetchAllRoles = async (): Promise<RoleItem[]> => {
  const response = await api.account.post<RoleItem[]>(
    API_ENDPOINTS.rolePermission.getAllRoles,
    {}
  );
  // API returns data array directly
  return response.data || [];
};

// Hook for fetching all roles
export const useAllRoles = (enabled = true) => {
  return useQuery({
    queryKey: [...profileKeys.all, 'roles'],
    queryFn: fetchAllRoles,
    enabled,
    staleTime: 1000 * 60 * 30, // 30 minutes - roles rarely change
  });
};

// Fetch default permissions by type
export const fetchDefaultPermissions = async (): Promise<PermissionItem[]> => {
  const response = await api.admin.post<GetDefaultPermissionsResponse>(
    API_ENDPOINTS.admin.getDefaultPermissionsByType,
    {
      name: 'college',
      apiSecurityKey: import.meta.env.VITE_API_SECURITY_KEY || '',
    }
  );
  return response.data?.permissions || [];
};

// Hook for fetching default permissions
export const useDefaultPermissions = (enabled = true) => {
  return useQuery({
    queryKey: [...profileKeys.all, 'defaultPermissions'],
    queryFn: fetchDefaultPermissions,
    enabled,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

// Fetch user permissions by user UId
export const fetchUserPermissions = async (userUId: string): Promise<UserPermissionsResponse> => {
  const response = await api.admin.post<UserPermissionsResponse>(
    API_ENDPOINTS.admin.getUserPermissionsByUserUId,
    { uId: userUId }
  );
  return response.data;
};

// Hook for fetching user permissions
export const useUserPermissions = (userUId: string | null, enabled = true) => {
  return useQuery({
    queryKey: [...profileKeys.all, 'userPermissions', userUId],
    queryFn: () => fetchUserPermissions(userUId!),
    enabled: enabled && !!userUId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Invite placement coordinator mutation
export const useInvitePlacementCoordinator = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InvitePlacementCoordinatorRequest) => {
      const response = await api.college.post(
        API_ENDPOINTS.college.invitePlacementCoordinator,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.collegeDetails() });
    },
  });
};

// Delete placement coordinator mutation
export const useDeletePlacementCoordinator = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (uId: string) => {
      const response = await api.college.post(
        API_ENDPOINTS.college.deletePlacementCoordinator,
        { uId }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.collegeDetails() });
    },
  });
};

// Update user permissions mutation
export const useUpdateUserPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UserPermissionsResponse) => {
      const response = await api.admin.post(
        API_ENDPOINTS.admin.updateUserPermissions,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...profileKeys.all, 'userPermissions'] });
    },
  });
};

// ===== TPO SETTINGS APIs =====

// Fetch TPO settings
export const fetchTpoSettings = async (): Promise<TpoSettingsModel> => {
  const organizationUId = authService.getOrganizationUid();

  if (!organizationUId) {
    throw new Error('Organization UID not found');
  }

  const response = await api.college.post<TpoSettingsModel>(
    API_ENDPOINTS.college.getPlacementCoordinatorSettingByCollegeUId,
    { uId: organizationUId }
  );
  return response.data;
};

// Hook for fetching TPO settings
export const useTpoSettings = (enabled = true) => {
  return useQuery({
    queryKey: [...profileKeys.all, 'tpoSettings'],
    queryFn: fetchTpoSettings,
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Update TPO settings mutation
export const useUpdateTpoSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TpoSettingsModel) => {
      const response = await api.college.post<TpoSettingsModel>(
        API_ENDPOINTS.college.updatePlacementCoordinatorSetting,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...profileKeys.all, 'tpoSettings'] });
    },
  });
};
